import { type UnitSystem } from './admin';
import { UnitConverter } from '@/hooks/useUnitSystem';

/**
 * Maps question IDs to their unit-specific configurations
 * Add questions here that should adapt based on user's unit system preference
 */
interface UnitAwareQuestion {
  questionId: string;
  textTemplate?: (unitSystem: UnitSystem) => string;
  optionsTemplate?: (unitSystem: UnitSystem) => string[];
}

/**
 * Configuration for unit-aware questions
 * Questions that contain measurements and should adapt to user's preference
 */
export const UNIT_AWARE_QUESTIONS: Record<string, UnitAwareQuestion> = {
  // Temperature preference for sleeping
  'daily.4': {
    questionId: 'daily.4',
    optionsTemplate: (unitSystem: UnitSystem) => {
      if (unitSystem === 'metric') {
        return [
          'Below 18°C',
          '18-20°C',
          '20-21°C',
          '21-22°C',
          '22-24°C',
          'Above 24°C'
        ];
      }
      // Imperial (default)
      return [
        'Below 65°F',
        '65-68°F',
        '68-70°F',
        '70-72°F',
        '72-75°F',
        'Above 75°F'
      ];
    }
  }

  // Add more unit-aware questions here as they're discovered
  // Example patterns for future questions:

  // Height question (if it exists):
  // 'physical.height': {
  //   textTemplate: (unitSystem) => unitSystem === 'metric'
  //     ? 'What is your height? (in meters/centimeters)'
  //     : 'What is your height? (in feet/inches)',
  //   optionsTemplate: (unitSystem) => {
  //     if (unitSystem === 'metric') {
  //       return ['Under 150cm', '150-160cm', '160-170cm', '170-180cm', '180-190cm', 'Over 190cm'];
  //     }
  //     return ['Under 5\'0"', '5\'0"-5\'4"', '5\'4"-5\'8"', '5\'8"-6\'0"', '6\'0"-6\'4"', 'Over 6\'4"'];
  //   }
  // },

  // Weight question (if it exists):
  // 'physical.weight': {
  //   textTemplate: (unitSystem) => unitSystem === 'metric'
  //     ? 'What is your approximate weight range? (in kilograms)'
  //     : 'What is your approximate weight range? (in pounds)',
  //   optionsTemplate: (unitSystem) => {
  //     if (unitSystem === 'metric') {
  //       return ['Under 50kg', '50-60kg', '60-70kg', '70-80kg', '80-90kg', 'Over 90kg'];
  //     }
  //     return ['Under 110lbs', '110-130lbs', '130-150lbs', '150-170lbs', '170-190lbs', 'Over 190lbs'];
  //   }
  // },

  // Distance question (if it exists):
  // 'living.commute': {
  //   textTemplate: (unitSystem) => unitSystem === 'metric'
  //     ? 'How far is your typical commute? (in kilometers)'
  //     : 'How far is your typical commute? (in miles)',
  //   optionsTemplate: (unitSystem) => {
  //     if (unitSystem === 'metric') {
  //       return ['Under 5km', '5-10km', '10-20km', '20-40km', 'Over 40km', 'Work from home'];
  //     }
  //     return ['Under 3mi', '3-6mi', '6-12mi', '12-25mi', 'Over 25mi', 'Work from home'];
  //   }
  // }
};

/**
 * Adapts a question's text and options based on user's unit system preference
 *
 * @param question - The question object to adapt
 * @param unitSystem - User's preferred unit system ('imperial' or 'metric')
 * @returns Adapted question with unit-appropriate text and options
 */
export function adaptQuestionUnits<T extends { id: string; text: string; options?: string[] }>(
  question: T,
  unitSystem: UnitSystem
): T {
  const config = UNIT_AWARE_QUESTIONS[question.id];

  if (!config) {
    // Question doesn't need unit adaptation
    return question;
  }

  const adapted = { ...question };

  // Adapt question text if template provided
  if (config.textTemplate) {
    adapted.text = config.textTemplate(unitSystem);
  }

  // Adapt options if template provided
  if (config.optionsTemplate && question.options) {
    adapted.options = config.optionsTemplate(unitSystem);
  }

  return adapted;
}

/**
 * Adapts an array of questions based on user's unit system preference
 *
 * @param questions - Array of questions to adapt
 * @param unitSystem - User's preferred unit system
 * @returns Array of adapted questions
 */
export function adaptQuestionsUnits<T extends { id: string; text: string; options?: string[] }>(
  questions: T[],
  unitSystem: UnitSystem
): T[] {
  return questions.map(q => adaptQuestionUnits(q, unitSystem));
}

/**
 * Checks if a question ID is unit-aware (needs adaptation)
 */
export function isUnitAwareQuestion(questionId: string): boolean {
  return questionId in UNIT_AWARE_QUESTIONS;
}

/**
 * Gets the list of all unit-aware question IDs
 */
export function getUnitAwareQuestionIds(): string[] {
  return Object.keys(UNIT_AWARE_QUESTIONS);
}

/**
 * Converts a response value from one unit system to another
 * Used when displaying partner's response who may use different unit system
 *
 * @param questionId - The question being answered
 * @param value - The response value
 * @param fromSystem - The unit system the value is in
 * @param toSystem - The unit system to convert to
 * @returns Converted value with appropriate label
 */
export function convertResponseUnits(
  questionId: string,
  value: string,
  fromSystem: UnitSystem,
  toSystem: UnitSystem
): string {
  // If same system or question doesn't need conversion, return as-is
  if (fromSystem === toSystem || !isUnitAwareQuestion(questionId)) {
    return value;
  }

  // Temperature conversions for 'daily.4'
  if (questionId === 'daily.4') {
    // Map imperial ranges to metric ranges
    const imperialToMetric: Record<string, string> = {
      'Below 65°F': 'Below 18°C',
      '65-68°F': '18-20°C',
      '68-70°F': '20-21°C',
      '70-72°F': '21-22°C',
      '72-75°F': '22-24°C',
      'Above 75°F': 'Above 24°C'
    };

    // Map metric ranges to imperial ranges
    const metricToImperial: Record<string, string> = {
      'Below 18°C': 'Below 65°F',
      '18-20°C': '65-68°F',
      '20-21°C': '68-70°F',
      '21-22°C': '70-72°F',
      '22-24°C': '72-75°F',
      'Above 24°C': 'Above 75°F'
    };

    if (fromSystem === 'imperial' && toSystem === 'metric') {
      return imperialToMetric[value] || value;
    } else if (fromSystem === 'metric' && toSystem === 'imperial') {
      return metricToImperial[value] || value;
    }
  }

  // Add more conversion logic for other unit-aware questions here

  return value;
}

/**
 * Helper function to format a numeric value with appropriate unit
 *
 * @param value - Numeric value to format
 * @param type - Type of measurement
 * @param unitSystem - Unit system to use
 * @returns Formatted string with unit
 */
export function formatWithUnit(
  value: number,
  type: 'temperature' | 'distance' | 'weight' | 'height' | 'volume',
  unitSystem: UnitSystem
): string {
  switch (type) {
    case 'temperature':
      return UnitConverter.temperature.format(value, unitSystem);
    case 'distance':
      return UnitConverter.distance.format(value, unitSystem);
    case 'weight':
      return UnitConverter.weight.format(value, unitSystem);
    case 'volume':
      return UnitConverter.volume.format(value, unitSystem);
    case 'height':
      return UnitConverter.height.formatShort(value, unitSystem);
    default:
      return value.toString();
  }
}